import { useEffect, useState } from 'react';
import {
  Navigate,
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { getProject, type Project } from '../lib/projects';
import { ApiError } from '../lib/api';
import { ProjectTabs } from '../components/projects/ProjectTabs';
import { ProjectForm } from '../components/projects/ProjectForm';
import { StatusBadge } from '../components/projects/StatusBadge';
import { Alert, BackLink, EmptyState, LinkButton, PageHeader } from '../components/ui';
import { ChartIcon, PencilIcon } from '../components/icons';

type ProjectDetailContext = {
  project: Project;
  setProject: (project: Project) => void;
};

/** Typed access to what the shell loaded, so tabs never refetch the project. */
export const useProjectDetail = () => useOutletContext<ProjectDetailContext>();

/**
 * The shell around a single project: it owns the fetch, the header, and the tab
 * bar. Each tab is a nested route, so a section is linkable and the back button
 * steps between them.
 */
export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();

  const [project, setProject] = useState<Project | null>(null);
  // Only the title depends on this now -- see the comment on the return below.
  const [, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();

    setLoading(true);
    setError(null);
    setNotFound(false);

    getProject(id, controller.signal)
      .then(({ project: loaded }) => setProject(loaded))
      .catch((loadError: unknown) => {
        if (controller.signal.aborted) return;
        if (loadError instanceof ApiError && loadError.status === 404) {
          setNotFound(true);
          return;
        }
        setError(loadError instanceof Error ? loadError.message : 'Could not load this project.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [id]);

  if (notFound) {
    return (
      <EmptyState
        title="Project not found"
        action={<LinkButton to="/projects">Back to projects</LinkButton>}
      >
        It may have been deleted.
      </EmptyState>
    );
  }

  if (error) return <Alert>{error}</Alert>;

  /*
   * The chrome renders while the project is still in flight.
   *
   * This used to return one line of grey text for the *whole* page, so the back
   * link, the title and the tab bar all appeared from nothing a moment later and
   * pushed the section below them down the screen. The header is the same shape
   * either way; only the name is unknown, so only the name is a skeleton.
   */
  return (
    <div>
      <PageHeader
        eyebrow={<BackLink to="/projects">Projects</BackLink>}
        title={
          project ? (
            project.name
          ) : (
            <span
              aria-hidden="true"
              className="inline-block h-6 w-56 max-w-full animate-pulse rounded bg-stone-200 align-middle"
            />
          )
        }
        meta={project ? <StatusBadge status={project.status} /> : null}
      />

      <div className="mt-6">
        <ProjectTabs />
      </div>

      <div className="mt-6">
        {project ? (
          <Outlet context={{ project, setProject } satisfies ProjectDetailContext} />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Any unknown section falls back to the project's default tab, which the index
 * route in App.tsx decides. The target has to be absolute: a relative `to`
 * resolves against the *unmatched* path, so a relative one would append a
 * segment to the bad URL and redirect forever.
 */
export function ProjectSectionFallback() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/projects/${id}`} replace />;
}

export function ProjectOverview() {
  return (
    <EmptyState icon={<ChartIcon className="h-5 w-5" />} title="Overview coming soon">
      Project stats will appear here.
    </EmptyState>
  );
}

export function ProjectEdit() {
  const { project, setProject } = useProjectDetail();
  const navigate = useNavigate();

  // Capped like every other form in the app. This one ran the full width of the
  // page while the assignee editor two clicks away stopped at `max-w-2xl`.
  return (
    <div className="max-w-2xl">
      <ProjectForm
        // Remount if the shell ever swaps in a different project underneath.
        key={project.id}
        project={project}
        onSaved={setProject}
        onCancel={() => navigate(`/projects/${project.id}`)}
        onDeleted={() => navigate('/projects')}
      />
    </div>
  );
}

export function ProjectWhiteboard() {
  return (
    <EmptyState icon={<PencilIcon className="h-5 w-5" />} title="Whiteboard coming soon">
      A space to sketch this project out.
    </EmptyState>
  );
}
